import { useEffect } from 'react';
import { redirectToLogout, scheduleIdleLogout } from '@goodsteward/gs-auth-spa';
import { useAuthStore } from '@/stores/auth';
import { useIdleTimeoutMinutes } from '@/api/settings';
import { getCognitoConfig } from '@/auth/config';

/*
 * HIPAA §164.312(a)(2)(iii) — client-side idle-session timeout. Watches for
 * user interaction on the document; if `timeoutMinutes` passes with no
 * activity, calls `logout()` on the auth store. The server's JWT also has
 * an absolute lifetime (`sessionMaxMinutes`) — they are independent: the
 * server enforces an upper bound, this hook enforces the "stepped away from
 * the workstation" bound.
 *
 * After clearing local state, bounce through the IdP /logout so the
 * SSO cookie on auth.goodstewardaba.com is dropped. Without that step,
 * the next sign-in click would silently re-mint a token from the
 * surviving SSO cookie (no password, no MFA) — defeating the
 * idle-timeout requirement on a shared workstation. In dev the stub
 * IdP has no SSO cookie but exposes /logout that just bounces, so the
 * SPA can take one path regardless of mode.
 *
 * Phase 18.5 N4: scheduleIdleLogout from gs-auth-spa schedules an
 * additional timer at the access token's `exp`, whichever fires first.
 * Without that, a still-active user past the access-token TTL keeps
 * resetting the idle timer and the next API call 401s. Same logic now
 * lives in gs-auth-spa so admin / pma / home all benefit at once.
 *
 * Intentionally quiet: no warning dialog, no countdown. Future work can
 * layer those on top — the core requirement is that the auth token stops
 * being usable from this browser tab after the configured idle window.
 */

const ACTIVITY_EVENTS: readonly (keyof DocumentEventMap)[] = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'wheel',
];

export function useIdleLogout() {
    const token = useAuthStore((s) => s.token);
    const timeoutMinutes = useIdleTimeoutMinutes();
    const timeoutMs = timeoutMinutes * 60_000;

    useEffect(() => {
        if (!token) return;

        const ctrl = scheduleIdleLogout({
            token,
            idleTimeoutMs: timeoutMs,
            onLogout: () => {
                // Re-read from the store — token may have already been
                // cleared by a 401 interceptor or an explicit sign-out
                // in another tab. The gs-auth-spa primitive guarantees
                // onLogout fires at most once, but this guard keeps
                // the redirect / sessionStorage write quiet when the
                // token was already gone.
                if (!useAuthStore.getState().token) return;
                try {
                    sessionStorage.setItem('gs.signout.reason', 'idle');
                } catch { /* private mode / disabled storage — fall through */ }
                // Do NOT call logout() before the redirect. setState
                // triggers a re-render where RequireAuth sees no token,
                // fires redirectToHostedUi, and that second
                // window.location.assign cancels the /logout navigation.
                // The redirect tears down the SPA on its own.
                redirectToLogout(getCognitoConfig());
            },
        });

        function handleActivity() {
            if (document.visibilityState === 'hidden') return;
            ctrl.reset();
        }

        // Returning to the foreground is NOT activity — it must not extend
        // the idle window. iOS Safari freezes JS timers while the tab is
        // backgrounded, so a user who switches to another app for longer
        // than the idle window would, on return, have the frozen timer
        // resume (late) *and* fire `visibilitychange`. Treating that as
        // activity (ctrl.reset) would hand them a fresh full window and
        // defeat the timeout on a shared iPad. Instead re-check the window
        // against the wall clock: ctrl.check() logs out immediately if it
        // already lapsed.
        function handleVisibility() {
            if (document.visibilityState === 'visible') ctrl.check();
        }

        for (const evt of ACTIVITY_EVENTS) {
            document.addEventListener(evt, handleActivity, { passive: true });
        }
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            ctrl.clear();
            for (const evt of ACTIVITY_EVENTS) {
                document.removeEventListener(evt, handleActivity);
            }
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [token, timeoutMs]);
}
